import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import {
  Wrench, User, ClipboardList, PackageCheck, Clock, Send, CheckCircle,
  AlertCircle, ChevronRight, Pencil, Trash2, Package, Images, Printer,
  X, Plus, Lock,
} from 'lucide-react'
import { serviceAuftraege as serviceApi, ersatzteile as ersatzteileApi } from '../../lib/api'
import { printServiceLabel } from '../../lib/printLabel'
import { TopBar } from '../../components/layout/TopBar'
import { Card, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { FotoUpload } from '../../components/ui/FotoUpload'
import { LoadingSpinner, ErrorMessage } from '../../components/ui/EmptyState'
import { formatDatum } from '../../lib/utils'
import { cn } from '../../lib/utils'
import type { ServiceAuftrag, ServiceErsatzteilPosition } from '../../types'

const STUNDENSATZ = 110

const STATUS_FLOW: ServiceAuftrag['status'][] = ['offen', 'in_bearbeitung', 'abgeschlossen', 'versendet']

const STATUS_CONFIG: Record<ServiceAuftrag['status'], { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  offen:          { label: 'Offen',          color: 'text-gray-600',   bg: 'bg-gray-100',    icon: <AlertCircle size={14} /> },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'text-blue-700',   bg: 'bg-blue-100',    icon: <Clock size={14} /> },
  abgeschlossen:  { label: 'Abgeschlossen',  color: 'text-green-700',  bg: 'bg-green-100',   icon: <CheckCircle size={14} /> },
  versendet:      { label: 'Versendet',       color: 'text-purple-700', bg: 'bg-purple-100',  icon: <Send size={14} /> },
}

const NEXT_STATUS_LABEL: Partial<Record<ServiceAuftrag['status'], string>> = {
  offen: 'In Bearbeitung setzen',
  in_bearbeitung: 'Als abgeschlossen markieren',
  abgeschlossen: 'Als versendet markieren',
}

function minutesToLabel(min: number) {
  if (!min) return '–'
  const h = Math.floor(min / 60)
  const m = min % 60
  return h > 0 ? `${h}h ${m > 0 ? m + 'min' : ''}`.trim() : `${m}min`
}

interface EditForm {
  status: ServiceAuftrag['status']
  garantie: boolean
  beschreibung: string
  reparatur_log: string
  was_ersetzt: string
  stunden: string
  minuten: string
  kostenManuell: string
  verrechnet: boolean
  bexio_rechnung_id: string
  ruecksendung_datum: string
  ruecksendung_tracking: string
  ruecksendung_notizen: string
}

function dataToForm(d: ServiceAuftrag): EditForm {
  return {
    status: d.status,
    garantie: !!d.garantie,
    beschreibung: d.beschreibung ?? '',
    reparatur_log: d.reparatur_log ?? '',
    was_ersetzt: d.was_ersetzt ?? '',
    stunden: Math.floor((d.zeit_minuten ?? 0) / 60).toString(),
    minuten: ((d.zeit_minuten ?? 0) % 60).toString(),
    kostenManuell: '',
    verrechnet: !!d.verrechnet,
    bexio_rechnung_id: d.bexio_rechnung_id ?? '',
    ruecksendung_datum: d.ruecksendung_datum ?? '',
    ruecksendung_tracking: d.ruecksendung_tracking ?? '',
    ruecksendung_notizen: d.ruecksendung_notizen ?? '',
  }
}

export function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState<EditForm | null>(null)
  const [editErsatzteile, setEditErsatzteile] = useState<ServiceErsatzteilPosition[]>([])

  // Ersatzteil add state
  const [addFromDb, setAddFromDb] = useState('')
  const [addDbMenge, setAddDbMenge] = useState(1)
  const [addCustom, setAddCustom] = useState({ bezeichnung: '', menge: 1, preis: 0 })
  const [addTab, setAddTab] = useState<'db' | 'manuell'>('db')

  const { data, isLoading, error } = useQuery({
    queryKey: ['service', id],
    queryFn: () => serviceApi.get(id!),
    enabled: !!id,
  })

  const { data: ersatzteileList } = useQuery({
    queryKey: ['ersatzteile'],
    queryFn: () => ersatzteileApi.list(),
    enabled: editMode,
  })

  const updateMutation = useMutation({
    mutationFn: (d: Partial<ServiceAuftrag>) => serviceApi.update(id!, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service', id] })
      qc.invalidateQueries({ queryKey: ['service'] })
      setEditMode(false)
    },
  })

  // Sync form when data loads or edit mode opens
  useEffect(() => {
    if (!data || !editMode || editForm) return
    setEditForm(dataToForm(data))
    try { setEditErsatzteile(JSON.parse(data.ersatzteile ?? '[]')) } catch { setEditErsatzteile([]) }
  }, [data, editMode, editForm])

  if (isLoading) return <><TopBar title="Service-Auftrag" backTo="/service" /><LoadingSpinner /></>
  if (error || !data) return <><TopBar title="Fehler" backTo="/service" /><ErrorMessage message="Auftrag nicht gefunden" /></>

  const isLocked = data.status === 'versendet'
  const sc = STATUS_CONFIG[data.status]
  const currentIdx = STATUS_FLOW.indexOf(data.status)
  const nextStatus = STATUS_FLOW[currentIdx + 1] as ServiceAuftrag['status'] | undefined

  const parsedErsatzteile: ServiceErsatzteilPosition[] = (() => {
    try { return JSON.parse(data.ersatzteile ?? '[]') } catch { return [] }
  })()
  const parsedFotos: string[] = (() => { try { return JSON.parse(data.fotos ?? '[]') } catch { return [] } })()

  // ── Edit mode cost calculation ──────────────────────────
  const ef = editForm
  const editZeitMin = ef ? (parseInt(ef.stunden || '0') * 60 + parseInt(ef.minuten || '0')) : 0
  const editZeitKosten = (editZeitMin / 60) * STUNDENSATZ
  const editErsatzteileTotal = editErsatzteile.reduce((s, e) => s + e.menge * e.einzel_preis, 0)
  const editAutoTotal = editZeitKosten + editErsatzteileTotal
  const editFinalKosten = ef && ef.kostenManuell !== '' ? parseFloat(ef.kostenManuell) || 0 : editAutoTotal

  const set = (k: keyof EditForm, v: unknown) => setEditForm((f) => f ? { ...f, [k]: v } : f)

  function enterEdit() {
    if (!data) return
    setEditForm(dataToForm(data))
    try { setEditErsatzteile(JSON.parse(data.ersatzteile ?? '[]')) } catch { setEditErsatzteile([]) }
    setAddFromDb('')
    setAddDbMenge(1)
    setAddCustom({ bezeichnung: '', menge: 1, preis: 0 })
    setEditMode(true)
  }

  function cancelEdit() {
    setEditMode(false)
    setEditForm(null)
  }

  function saveEdit() {
    if (!ef) return
    updateMutation.mutate({
      ...data,
      status: ef.status,
      garantie: ef.garantie ? 1 : 0,
      beschreibung: ef.beschreibung,
      reparatur_log: ef.reparatur_log,
      was_ersetzt: ef.was_ersetzt,
      zeit_minuten: editZeitMin,
      ersatzteile: JSON.stringify(editErsatzteile),
      kosten: editFinalKosten,
      verrechnet: ef.verrechnet ? 1 : 0,
      bexio_rechnung_id: ef.bexio_rechnung_id || null,
      ruecksendung_datum: ef.ruecksendung_datum || null,
      ruecksendung_tracking: ef.ruecksendung_tracking || null,
      ruecksendung_notizen: ef.ruecksendung_notizen || null,
    } as unknown as Partial<ServiceAuftrag>)
  }

  // ── Add Ersatzteil helpers ───────────────────────────────
  function addFromDbList() {
    const found = ersatzteileList?.find((e) => e.id === addFromDb)
    if (!found) return
    setEditErsatzteile((prev) => {
      const existing = prev.findIndex((e) => e.bezeichnung === found.bezeichnung && e.einzel_preis === (found.preis ?? 0))
      if (existing >= 0) {
        return prev.map((e, i) => i === existing ? { ...e, menge: e.menge + addDbMenge } : e)
      }
      return [...prev, { id: found.id, bezeichnung: found.bezeichnung, menge: addDbMenge, einzel_preis: found.preis ?? 0 }]
    })
    setAddFromDb('')
    setAddDbMenge(1)
  }

  function addManual() {
    if (!addCustom.bezeichnung.trim()) return
    setEditErsatzteile((prev) => [...prev, {
      id: crypto.randomUUID(),
      bezeichnung: addCustom.bezeichnung.trim(),
      menge: addCustom.menge,
      einzel_preis: addCustom.preis,
    }])
    setAddCustom({ bezeichnung: '', menge: 1, preis: 0 })
  }

  function removeErsatzteil(idx: number) {
    setEditErsatzteile((prev) => prev.filter((_, i) => i !== idx))
  }

  function changeMenge(idx: number, delta: number) {
    setEditErsatzteile((prev) => prev.map((e, i) => {
      if (i !== idx) return e
      const newMenge = Math.max(1, e.menge + delta)
      return { ...e, menge: newMenge }
    }))
  }

  // ── View mode (read-only) ──────────────────────────────
  const viewErsatzteileTotal = parsedErsatzteile.reduce((s, e) => s + e.menge * e.einzel_preis, 0)

  return (
    <>
      <TopBar
        title={data.nummer ?? 'Service-Auftrag'}
        subtitle={`${data.kunde_firma ?? data.kunde_name} · ${data.geraet_modell}`}
        backTo="/service"
        rightAction={
          editMode ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={cancelEdit}>Abbrechen</Button>
              <Button size="sm" loading={updateMutation.isPending} onClick={saveEdit}>Speichern</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" icon={<Printer size={14} />} onClick={() => printServiceLabel(data)}>
                Label
              </Button>
              {isLocked ? (
                <div className="flex items-center gap-1.5 text-xs text-purple-600 font-medium px-3 py-1.5 bg-purple-50 rounded-xl">
                  <Lock size={13} /> Versendet
                </div>
              ) : (
                <Button size="sm" icon={<Pencil size={14} />} onClick={enterEdit}>
                  Bearbeiten
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="px-4 py-3 space-y-4 pb-8">

        {/* ── Status ───────────────────────────────────────── */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            {editMode && ef ? (
              <Select
                value={ef.status}
                onChange={(e) => set('status', e.target.value)}
                options={[
                  { value: 'offen', label: 'Offen' },
                  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
                  { value: 'abgeschlossen', label: 'Abgeschlossen' },
                  { value: 'versendet', label: 'Versendet' },
                ]}
              />
            ) : (
              <div className={cn('flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full', sc.bg, sc.color)}>
                {sc.icon} {sc.label}
              </div>
            )}
            {editMode && ef ? (
              <Select
                value={ef.garantie ? '1' : '0'}
                onChange={(e) => set('garantie', e.target.value === '1')}
                options={[{ value: '0', label: '💳 Bezahlt' }, { value: '1', label: '🛡️ Garantie' }]}
              />
            ) : data.garantie ? (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">🛡️ Garantie</span>
            ) : (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">💳 Bezahlt</span>
            )}
          </div>

          {/* Progress bar — always visible */}
          <div className="flex items-center gap-0.5">
            {STATUS_FLOW.map((s, i) => {
              const conf = STATUS_CONFIG[s]
              const activeStatus = editMode && ef ? ef.status : data.status
              const activeIdx = STATUS_FLOW.indexOf(activeStatus)
              const done = i <= activeIdx
              return (
                <div key={s} className="flex-1 flex flex-col items-center gap-1">
                  <div className={cn(
                    'w-full h-1.5 rounded-full transition-all',
                    done ? (s === 'versendet' ? 'bg-purple-500' : s === 'abgeschlossen' ? 'bg-green-500' : 'bg-blue-500') : 'bg-gray-200'
                  )} />
                  <span className={cn('text-[9px] font-medium', done ? conf.color : 'text-gray-300')}>
                    {conf.label}
                  </span>
                </div>
              )
            })}
          </div>

          {/* 1-click status advance — only in view mode and not locked */}
          {!editMode && !isLocked && nextStatus && (
            <Button
              className="mt-3"
              fullWidth
              size="sm"
              icon={<ChevronRight size={14} />}
              loading={updateMutation.isPending}
              onClick={() => updateMutation.mutate({ ...data, status: nextStatus })}
            >
              {NEXT_STATUS_LABEL[data.status]}
            </Button>
          )}
        </Card>

        {/* ── Auftragsinformationen ─────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><User size={15} className="inline mr-1.5" />Auftrag</CardTitle></CardHeader>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Kunde</p>
              <p className="font-medium text-gray-900">{data.kunde_firma ?? data.kunde_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Gerät</p>
              <p className="font-medium text-gray-900">{data.geraet_modell}</p>
            </div>
            {data.seriennummer && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Seriennummer</p>
                <p className="font-medium font-mono text-gray-900">{data.seriennummer}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Eröffnet</p>
              <p className="font-medium text-gray-900">{formatDatum(data.erstellt_am)}</p>
            </div>
          </div>
        </Card>

        {/* ── Fehlermeldung ─────────────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><Wrench size={15} className="inline mr-1.5" />Fehlermeldung</CardTitle></CardHeader>
          {editMode && ef ? (
            <Textarea
              value={ef.beschreibung}
              onChange={(e) => set('beschreibung', e.target.value)}
              rows={3}
              placeholder="Beschreibung des Defekts..."
            />
          ) : (
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{data.beschreibung}</p>
          )}
        </Card>

        {/* ── Reparatur ────────────────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><ClipboardList size={15} className="inline mr-1.5" />Reparatur</CardTitle></CardHeader>
          {editMode && ef ? (
            <div className="space-y-4">
              <Textarea label="Reparatur-Log" value={ef.reparatur_log}
                onChange={(e) => set('reparatur_log', e.target.value)} rows={4}
                placeholder="Was wurde gemacht, welche Schritte..." />
              <Textarea label="Was wurde ersetzt" value={ef.was_ersetzt}
                onChange={(e) => set('was_ersetzt', e.target.value)} rows={2}
                placeholder="Ersetzte Teile, Komponenten..." />
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">Benötigte Zeit</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input label="Stunden" type="number" min="0" value={ef.stunden}
                    onChange={(e) => set('stunden', e.target.value)} />
                  <Input label="Minuten" type="number" min="0" max="59" value={ef.minuten}
                    onChange={(e) => set('minuten', e.target.value)} />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {data.reparatur_log ? (
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Reparatur-Log</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{data.reparatur_log}</p>
                </div>
              ) : <p className="text-sm text-gray-400 italic">Noch kein Reparatur-Log</p>}
              {data.was_ersetzt && (
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Was wurde ersetzt</p>
                  <p className="text-sm text-gray-800">{data.was_ersetzt}</p>
                </div>
              )}
              {data.zeit_minuten > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Benötigte Zeit</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                    <Clock size={13} className="text-gray-400" />{minutesToLabel(data.zeit_minuten)}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* ── Ersatzteile ──────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>
              <Package size={15} className="inline mr-1.5" />
              Ersatzteile {editMode ? `(${editErsatzteile.length})` : parsedErsatzteile.length > 0 ? `(${parsedErsatzteile.length})` : ''}
            </CardTitle>
          </CardHeader>

          {editMode ? (
            <div className="space-y-3">
              {/* Current list */}
              {editErsatzteile.length > 0 ? (
                <div className="space-y-2">
                  {editErsatzteile.map((teil, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{teil.bezeichnung}</p>
                        <p className="text-xs text-gray-400">CHF {teil.einzel_preis.toFixed(2)} / Stück</p>
                      </div>
                      {/* Menge controls */}
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => changeMenge(i, -1)}
                          className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100">
                          <span className="text-lg leading-none mb-0.5">−</span>
                        </button>
                        <span className="text-sm font-bold w-5 text-center">{teil.menge}</span>
                        <button onClick={() => changeMenge(i, +1)}
                          className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100">
                          <Plus size={14} />
                        </button>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 w-16 text-right">
                        CHF {(teil.menge * teil.einzel_preis).toFixed(2)}
                      </p>
                      <button onClick={() => removeErsatzteil(i)}
                        className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-red-400 active:bg-red-100 shrink-0">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <p className="text-sm font-semibold text-gray-700">Total Ersatzteile</p>
                    <p className="text-sm font-bold text-gray-900">CHF {editErsatzteileTotal.toFixed(2)}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic text-center py-2">Noch keine Ersatzteile</p>
              )}

              {/* Add section */}
              <div className="border border-dashed border-gray-200 rounded-2xl p-3 space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => setAddTab('db')}
                    className={cn('flex-1 text-xs font-medium py-1.5 rounded-xl transition-colors',
                      addTab === 'db' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600')}
                  >
                    Aus Lager
                  </button>
                  <button
                    onClick={() => setAddTab('manuell')}
                    className={cn('flex-1 text-xs font-medium py-1.5 rounded-xl transition-colors',
                      addTab === 'manuell' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600')}
                  >
                    Manuell
                  </button>
                </div>

                {addTab === 'db' ? (
                  <div className="space-y-2">
                    <Select
                      value={addFromDb}
                      onChange={(e) => setAddFromDb(e.target.value)}
                      placeholder="Ersatzteil auswählen..."
                      options={(ersatzteileList ?? []).map((e) => ({
                        value: e.id,
                        label: `${e.bezeichnung}${e.artikelnummer ? ` (${e.artikelnummer})` : ''} – CHF ${(e.preis ?? 0).toFixed(2)}`,
                      }))}
                    />
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Input label="Menge" type="number" min="1" value={addDbMenge.toString()}
                          onChange={(e) => setAddDbMenge(Math.max(1, parseInt(e.target.value) || 1))} />
                      </div>
                      <Button size="sm" icon={<Plus size={14} />} onClick={addFromDbList} disabled={!addFromDb}>
                        Hinzufügen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input label="Bezeichnung" value={addCustom.bezeichnung}
                      onChange={(e) => setAddCustom((c) => ({ ...c, bezeichnung: e.target.value }))}
                      placeholder="z.B. Dichtungsring 8mm" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input label="Menge" type="number" min="1" value={addCustom.menge.toString()}
                        onChange={(e) => setAddCustom((c) => ({ ...c, menge: Math.max(1, parseInt(e.target.value) || 1) }))} />
                      <Input label="Preis (CHF)" type="number" step="0.05" min="0" value={addCustom.preis.toString()}
                        onChange={(e) => setAddCustom((c) => ({ ...c, preis: parseFloat(e.target.value) || 0 }))} />
                    </div>
                    <Button size="sm" fullWidth icon={<Plus size={14} />} onClick={addManual} disabled={!addCustom.bezeichnung.trim()}>
                      Manuell hinzufügen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            parsedErsatzteile.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Keine Ersatzteile erfasst</p>
            ) : (
              <div className="space-y-2">
                {parsedErsatzteile.map((teil, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{teil.bezeichnung}</p>
                      <p className="text-xs text-gray-400">CHF {teil.einzel_preis.toFixed(2)} × {teil.menge}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">CHF {(teil.menge * teil.einzel_preis).toFixed(2)}</p>
                  </div>
                ))}
                {viewErsatzteileTotal > 0 && (
                  <div className="flex justify-between pt-1 border-t border-gray-200">
                    <p className="text-sm font-semibold text-gray-700">Total Ersatzteile</p>
                    <p className="text-sm font-bold text-gray-900">CHF {viewErsatzteileTotal.toFixed(2)}</p>
                  </div>
                )}
              </div>
            )
          )}
        </Card>

        {/* ── Kosten & Verrechnung ─────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><PackageCheck size={15} className="inline mr-1.5" />Kosten & Verrechnung</CardTitle></CardHeader>
          {editMode && ef ? (
            <div className="space-y-4">
              {/* Auto-calculated breakdown */}
              <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Zeit ({minutesToLabel(editZeitMin)} × CHF {STUNDENSATZ}/h)</span>
                  <span>CHF {editZeitKosten.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Ersatzteile</span>
                  <span>CHF {editErsatzteileTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1.5">
                  <span>Total (auto)</span>
                  <span>CHF {editAutoTotal.toFixed(2)}</span>
                </div>
              </div>
              <Input
                label="Manuelle Anpassung (CHF)"
                type="number" step="0.05" min="0"
                value={ef.kostenManuell}
                onChange={(e) => set('kostenManuell', e.target.value)}
                placeholder={`Auto: ${editAutoTotal.toFixed(2)}`}
              />
              {ef.kostenManuell !== '' && (
                <p className="text-xs text-amber-600 -mt-2">
                  Manuell überschrieben: CHF {editFinalKosten.toFixed(2)}
                </p>
              )}
              <Select label="Verrechnet" value={ef.verrechnet ? '1' : '0'}
                onChange={(e) => set('verrechnet', e.target.value === '1')}
                options={[{ value: '0', label: 'Noch offen' }, { value: '1', label: 'Verrechnet ✓' }]} />
              <Input label="Bexio Rechnungs-Nr." value={ef.bexio_rechnung_id}
                onChange={(e) => set('bexio_rechnung_id', e.target.value)}
                placeholder="z.B. RE-2025-042" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Gesamtkosten</p>
                <p className="text-lg font-bold text-gray-900">CHF {(data.kosten ?? 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Status</p>
                {data.verrechnet ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                    <CheckCircle size={11} /> Verrechnet
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-700">
                    <AlertCircle size={11} /> Ausstehend
                  </span>
                )}
              </div>
              {data.bexio_rechnung_id && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-0.5">Bexio Rechnungs-Nr.</p>
                  <p className="font-medium font-mono text-gray-900">{data.bexio_rechnung_id}</p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* ── Rücksendung ──────────────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><Send size={15} className="inline mr-1.5" />Rücksendung</CardTitle></CardHeader>
          {editMode && ef ? (
            <div className="space-y-3">
              <Input label="Versanddatum" type="date" value={ef.ruecksendung_datum}
                onChange={(e) => set('ruecksendung_datum', e.target.value)} />
              <Input label="Tracking-Nummer" value={ef.ruecksendung_tracking}
                onChange={(e) => set('ruecksendung_tracking', e.target.value)}
                placeholder="z.B. 99.00.123456.78" />
              <Textarea label="Notizen" value={ef.ruecksendung_notizen}
                onChange={(e) => set('ruecksendung_notizen', e.target.value)} rows={2} />
            </div>
          ) : (data.ruecksendung_datum || data.ruecksendung_tracking || data.ruecksendung_notizen) ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              {data.ruecksendung_datum && (
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Versanddatum</p>
                  <p className="font-medium text-gray-900">{formatDatum(data.ruecksendung_datum)}</p>
                </div>
              )}
              {data.ruecksendung_tracking && (
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Tracking-Nr.</p>
                  <p className="font-medium font-mono text-gray-900">{data.ruecksendung_tracking}</p>
                </div>
              )}
              {data.ruecksendung_notizen && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-0.5">Notizen</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.ruecksendung_notizen}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">Noch keine Rücksendung erfasst</p>
          )}
        </Card>

        {/* ── Fotos ─────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><Images size={16} className="inline mr-2" />Fotos ({parsedFotos.length})</CardTitle>
          </CardHeader>
          <FotoUpload
            uploadFolder={`service/${id}`}
            fotos={parsedFotos}
            onChange={(newFotos) => updateMutation.mutate({ ...data, fotos: JSON.stringify(newFotos) })}
            readOnly={isLocked}
          />
        </Card>

        {/* ── Löschen ──────────────────────────────────────── */}
        {!editMode && !isLocked && (
          <Button
            variant="danger" fullWidth size="sm" icon={<Trash2 size={14} />}
            onClick={() => {
              if (confirm('Service-Auftrag löschen?')) {
                serviceApi.delete(id!).then(() => {
                  qc.invalidateQueries({ queryKey: ['service'] })
                  navigate('/service')
                })
              }
            }}
          >
            Auftrag löschen
          </Button>
        )}

        {/* Save error */}
        {updateMutation.error && (
          <p className="text-sm text-red-600 text-center">{(updateMutation.error as Error).message}</p>
        )}
      </div>
    </>
  )
}
